import { Property, Survey } from "@/features/survey/types/survey.types";
import Analytics from "@/services/Analytics";
import {
  useGetSurveyCommentsQuery,
  usePostCommentMutation,
} from "@/services/commentsService";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldInput } from "@emerald-react/input";
import { EmeraldSideSheet } from "@emerald-react/side-sheet";
import React, { useEffect, useRef, useState } from "react";

interface CommentsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  property: Property;
  survey: Survey;
}

export const CommentsSheet: React.FC<CommentsSheetProps> = ({
  isOpen,
  onClose,
  property,
  survey,
}) => {
  // Get username from localStorage and remove any surrounding quotes
  const rawUserName = localStorage.getItem("displayName") || "";
  const storedUserName = rawUserName.replace(/^["']|["']$/g, ""); // Remove surrounding quotes

  const [commentText, setCommentText] = useState("");
  const [userNameInput, setUserNameInput] = useState(storedUserName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingComment, setPendingComment] = useState<{
    comment: string;
    userName: string;
    countdown: number;
  } | null>(null);
  const [countdownTimer, setCountdownTimer] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const commentsContainerRef = useRef<HTMLDivElement>(null);
  const pendingCommentRef = useRef<HTMLDivElement>(null);

  const [postComment] = usePostCommentMutation();

  // Get all survey comments and filter for this property
  const { data: surveyComments = [], refetch } = useGetSurveyCommentsQuery(
    survey.id,
    {
      skip: !survey.id,
      pollingInterval: 30000, // Poll every 30 seconds
    },
  );

  // Filter comments for this specific property
  const propertyComments = surveyComments.filter(
    (comment) =>
      comment.propertyId === property.id ||
      comment.propertyId === property.propertyId,
  );

  // Sort comments by creation date (oldest first)
  const sortedComments = [...propertyComments].sort((a, b) => {
    if (!a.createdOn && !b.createdOn) return 0;
    if (!a.createdOn) return 1;
    if (!b.createdOn) return -1;
    return new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime();
  });

  // Reset form when opening
  useEffect(() => {
    if (isOpen) {
      setCommentText("");
      // Use localStorage value when opening and remove any surrounding quotes
      const rawStoredName = localStorage.getItem("displayName") || "";
      const currentStoredName = rawStoredName.replace(/^["']|["']$/g, "");
      setUserNameInput(currentStoredName);
    } else {
      // Clean up timer when closing
      if (countdownTimer) {
        clearInterval(countdownTimer);
        setCountdownTimer(null);
      }
      setPendingComment(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (countdownTimer) {
      setIsSubmitting(true);
    } else {
      setIsSubmitting(false);
    }
  }, [countdownTimer]);

  // Update localStorage whenever userNameInput changes
  useEffect(() => {
    if (userNameInput.trim()) {
      localStorage.setItem("displayName", userNameInput.trim());
    }
  }, [userNameInput]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        setCommentText("");
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen, onClose]);

  // Scroll to pending comment when it appears
  useEffect(() => {
    if (
      pendingComment &&
      pendingCommentRef.current &&
      commentsContainerRef.current
    ) {
      setTimeout(() => {
        if (pendingCommentRef.current) {
          pendingCommentRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        }
      }, 100);
    }
  }, [pendingComment]);

  const handleSubmit = async () => {
    if (!commentText.trim() || !userNameInput.trim()) {
      return;
    }

    // Start countdown instead of immediate submission
    const comment = commentText.trim();
    const userName = userNameInput.trim();

    setPendingComment({
      comment,
      userName,
      countdown: 5,
    });

    // Clear the form immediately
    setCommentText("");

    // Start the countdown timer
    let timeLeft = 5;
    const timer = setInterval(() => {
      timeLeft--;
      setPendingComment((prev) =>
        prev ? { ...prev, countdown: timeLeft } : null,
      );

      if (timeLeft <= 0) {
        clearInterval(timer);
        // Actually submit the comment
        submitComment(comment, userName);
      }
    }, 1000);

    setCountdownTimer(timer as unknown as number);
  };

  const submitComment = async (comment: string, userName: string) => {
    let internal = false;
    if (userName) internal = true;
    const commData: object = {
      event: "commentedProperty",
      survey_id: survey?.id,
      project_id: survey.projectId,
      address: property.address1,
      coordinates: {
        latitude: property.latitude,
        longitude: property.longitude,
      },
      available_sf: property.availableSF,
      property_name: property.propertyName,
      date: Date(),
      internal,
      comment,
    };

    try {
      await postComment({
        comment,
        propertyId: property.id.toString(),
        surveyId: survey.id,
        userName,
      }).unwrap();

      Analytics.send(commData);

      // Refetch comments to show the new comment immediately
      await refetch();

      // Clear pending comment
      setPendingComment(null);
      setCountdownTimer(null);
    } catch (error) {
      console.error("Error posting comment:", error);
      // Could add error toast notification here
    }
  };

  const handleUndoComment = () => {
    // Store the pending comment data before clearing
    const savedComment = pendingComment?.comment || "";
    const savedUserName = pendingComment?.userName || "";

    // Clear the countdown timer
    if (countdownTimer) {
      clearInterval(countdownTimer);
      setCountdownTimer(null);
    }

    // Clear pending comment first
    setPendingComment(null);

    // Then restore the comment text to the form - use multiple approaches for reliability
    setTimeout(() => {
      setCommentText(savedComment);
      setUserNameInput(savedUserName);

      // Try to find the textarea directly and set its value as backup
      setTimeout(() => {
        const textareas = document.querySelectorAll("textarea");
        textareas.forEach((textarea) => {
          if (
            textarea.placeholder?.includes("comments") ||
            textarea.getAttribute("aria-label")?.includes("comments")
          ) {
            textarea.value = savedComment;
            textarea.focus();
            textarea.setSelectionRange(
              savedComment.length,
              savedComment.length,
            );
          }
        });

        // Also try the ref approach
        if (textareaRef.current) {
          textareaRef.current.value = savedComment;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(
            savedComment.length,
            savedComment.length,
          );
        }
      }, 100);
    }, 50);
  };

  const handleClose = () => {
    // Clean up any pending timers
    if (countdownTimer) {
      clearInterval(countdownTimer);
      setCountdownTimer(null);
    }
    setPendingComment(null);
    setCommentText("");
    onClose();
  };

  // Helper function to format date
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return (
        date.toLocaleDateString("en-US", {
          month: "long",
          day: "2-digit",
          year: "numeric",
        }) +
        ` at ${date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })}`
      );
    } catch {
      return dateString;
    }
  };

  return (
    <EmeraldSideSheet
      opened={isOpen}
      title="Comments"
      subTitle={`Address: ${property.address1 || property.propertyName || `Property ${property.id}`}`}
      position="right"
      closeOnOutsideClick={true}
      hasCloseButton={true}
      closeOnEscape={true}
      onClose={handleClose}
      className="comments-sheet"
      hasDivider={true}
      style={{ width: "360px" }}
    >
      <div className="flex flex-col h-full">
        {/* Comments Display Section */}
        {sortedComments.length > 0 || pendingComment ? (
          <div
            ref={commentsContainerRef}
            className="flex-1 overflow-y-auto p-4"
          >
            {sortedComments.map((comment, index) => (
              <div
                key={comment.commentId || index}
                className={`space-y-1 py-3 ${index < sortedComments.length - 1 || pendingComment ? "border-b border-gray-200" : ""}`}
              >
                {/* User name */}
                <div className="text-xs text-gray-500 font-medium">
                  {comment.userName}
                </div>
                {/* Comment text */}
                <div className="text-sm text-gray-900 font-medium">
                  {comment.comment}
                </div>

                {/* Date */}
                <div className="text-xs text-gray-500">
                  {formatDate(
                    comment.createdOn
                      ? new Date(comment.createdOn + "Z").toISOString()
                      : undefined,
                  )}
                </div>
              </div>
            ))}

            {/* Pending Comment - shown as if it's in the list */}
            {pendingComment && (
              <div ref={pendingCommentRef} className="space-y-1 py-3">
                {/* User name */}
                <div className="text-xs text-gray-500 font-medium">
                  {pendingComment.userName}
                </div>

                {/* Comment text */}
                <div className="text-sm text-gray-900 font-medium">
                  {pendingComment.comment}
                </div>

                {/* Date with Undo button */}
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    {formatDate(new Date().toISOString())}
                  </div>

                  <EmeraldButton
                    label={`Undo: ${pendingComment.countdown}`}
                    type="Light"
                    size="Small"
                    onClick={handleUndoComment}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
            Leave a Comments
          </div>
        )}

        {/* Form Section - Bottom */}
        <div className="sticky bottom-0 bg-white p-4 border-t border-gray-300 space-y-4">
          <EmeraldInput
            label="Name"
            type="text"
            value={userNameInput}
            onChange={(e) => setUserNameInput(e.target.value)}
            placeholder="Enter your name"
            required
          />

          {/* Comment textarea */}
          <EmeraldInput
            label="Enter comments here"
            type="textarea"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            rows={6}
            ref={textareaRef}
          />

          {/* Submit button */}
          <div className="flex justify-end">
            <EmeraldButton
              label="Submit"
              type="Primary"
              disabled={
                isSubmitting || !commentText.trim() || !userNameInput.trim()
              }
              onClick={handleSubmit}
            />
          </div>
        </div>
      </div>
    </EmeraldSideSheet>
  );
};

export default CommentsSheet;
